import React, { useState, useEffect } from 'react';
import './InputDocs.css';
import { collection, deleteDoc, addDoc, doc, getDocs, getDoc } from 'firebase/firestore'; 
import { db, auth } from '../../firebase/firebaseConfig';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { useParams } from 'react-router-dom'; 

const InputDocs = () => {
    const [documents, setDocuments] = useState([]);
    const [selectedDoc, setSelectedDoc] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');
    const [clienteAtual, setClienteAtual] = useState(null);
    const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 500);
    const userEmail = auth.currentUser?.email;
    const storage = getStorage();
    const allowedExtensions = ['pdf', 'doc', 'docx'];
    const [isUploading, setIsUploading] = useState(false);
    const { token } = useParams(); // Pega o token da URL

    // Função para buscar o cliente com base no token
    const validarToken = async () => {
        if (token) {
            try {
                const docRef = doc(db, 'tokens', token);
                const docSnap = await getDoc(docRef);

                if (docSnap.exists()) {
                    const data = docSnap.data();
                    const clienteId = data.clienteId;
                    const emailAdvogado = data.userEmail;

                    // Atualiza o cliente com base nas informações do token
                    setClienteAtual({ id: clienteId, email: emailAdvogado });

                    // Carrega os documentos do cliente
                    loadDocuments(clienteId, emailAdvogado);
                } else {
                    setErrorMessage('Token inválido ou expirado.');
                }
            } catch (error) {
                console.error('Erro ao validar token:', error);
                setErrorMessage('Erro ao validar token.');
            }
        }
    };

    // Carrega os documentos para o cliente
    const loadDocuments = async (clienteId, userEmail) => {
        if (userEmail && clienteId) {
            const querySnapshot = await getDocs(collection(db, `documentos/${userEmail}/${clienteId}`));
            const docsData = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setDocuments(docsData);
        } else {
            setDocuments([]);
        }
    };

    // Verifica mudanças a cada segundo, mas apenas atualiza o estado se o cliente ou a rota realmente mudaram
    useEffect(() => {
        if (token) {
            validarToken(); // Valida o token caso exista
        } else {
            const clienteSalvo = sessionStorage.getItem('clienteSelecionado');
            const cliente = clienteSalvo ? JSON.parse(clienteSalvo) : null;
            setClienteAtual(cliente); // Fallback com o cliente salvo
            loadDocuments(cliente?.id, userEmail);
        }
    }, [token, userEmail]);

    useEffect(() => {
        const handleResize = () => {
            setIsMobileView(window.innerWidth <= 500);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleUpload = async (event) => {
        const files = Array.from(event.target.files);
        let invalidFiles = false;

        if (!clienteAtual) {
            console.error('Nenhum cliente selecionado');
            return;
        }

        for (const file of files) {
            const fileExtension = file.name.split('.').pop().toLowerCase();

            if (allowedExtensions.includes(fileExtension)) {
                setIsUploading(true);
                try {
                    const storageRef = ref(storage, `${userEmail}/${clienteAtual.id}/${file.name}`);
                    await uploadBytes(storageRef, file);
                    const downloadURL = await getDownloadURL(storageRef);

                    await addDoc(collection(db, `documentos/${userEmail}/${clienteAtual.id}`), {
                        clienteId: clienteAtual.id,
                        name: file.name,
                        url: downloadURL
                    });

                 
                } catch (error) {
                    console.error('Erro ao salvar o documento: ', error);
                } finally {
                    setIsUploading(false);
                }
            } else {
                invalidFiles = true;
            }
        }

        if (invalidFiles) {
            setErrorMessage('Somente arquivos .pdf, .doc ou .docx são permitidos.');
        } else {
            setErrorMessage('');
        }

        loadDocuments(clienteAtual.id, userEmail);
    };

    const handleDocSelect = (doc) => {
        if (isMobileView) {
            const link = document.createElement('a');
            link.href = doc.url;
            link.download = doc.name;
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } else {
            setSelectedDoc(doc);
        }
    };

    const handleDeleteDoc = async (docIndex) => {
        const docToDelete = documents[docIndex];

        if (docToDelete && clienteAtual) {
            try {
                await deleteDoc(doc(db, `documentos/${userEmail}/${clienteAtual.id}`, docToDelete.id));
                const updatedDocs = documents.filter((_, index) => index !== docIndex);
                setDocuments(updatedDocs);
                if (selectedDoc === docToDelete) {
                    setSelectedDoc(null);
                }
             
            } catch (error) {
                console.error('Erro ao excluir o documento: ', error);
            }
        }
    };

    return (
        <div className='main-container-inputdoc'>
            <div className='esquerda-input-doc'>
                <h3 className='titulos-docs'>Documentos</h3>
                <ul className='document-list'>
                    {documents.length > 0 ? (
                        documents.map((doc, index) => (
                            <li key={index} className='document-item'>
                                <span onClick={() => handleDocSelect(doc)}>{doc.name}</span>
                                {!token && (
                                    <button
                                        className='delete-button'
                                        onClick={() => handleDeleteDoc(index)}
                                    >
                                        <FontAwesomeIcon icon={faTrashCan} />
                                    </button>
                                )}
                            </li>
                        ))
                    ) : (
                        <p className='text-inputdoc'>Nenhum documento encontrado.</p>
                    )}
                </ul>

                {errorMessage && <p className="error-message">{errorMessage}</p>}

                {!token && (
                    <div className='upload-button-container'>
                        <input
                            type='file'
                            multiple
                            onChange={handleUpload}
                            className='upload-input'
                            id='upload-docs'
                            accept=".pdf,.doc,.docx"
                        />
                        {isUploading ? (
                            <div className="uploading-feedback-doc">
                                <div className="spinner-doc"></div>
                                <p>Enviando arquivo...</p>
                            </div>
                        ) : (
                            <label htmlFor='upload-docs' className='upload-button'>
                                Enviar arquivo
                            </label>
                        )}
                    </div>
                )}
            </div>

            {!isMobileView && <div className='linha-input' />}

            {!isMobileView && (
                <div className='direita-input-doc'>
                    {selectedDoc ? (
                        <div className='container-document-viewer'>
                            <h3 className='titulos-docs'>{selectedDoc.name}</h3>
                            <iframe
                                src={selectedDoc.url}
                                title={selectedDoc.name}
                                className='document-iframe'
                                frameBorder='0'
                                allowFullScreen
                            />
                        </div>
                    ) : (
                        <p className='text-inputdoc'>Selecione um documento para visualizar.</p>
                    )}
                </div>
            )}
        </div>
    );
};

export default InputDocs;
